import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Plus, DoorOpen } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/components/StatusBadge";

const CreateDialog = ({ onClose, onDone }) => {
  const [f, setF] = useState({ name: "", description: "", oversight: false });
  const go = async () => { try { await api.post("/committees", f); toast.success("Committee created — now add its admin"); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="create-committee-dialog">
        <DialogHeader><DialogTitle className="font-display">New committee room</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Committee name (e.g. Welfare Committee)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="committee-name-input" />
          <Textarea placeholder="Purpose (optional)" rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="committee-description-input" />
          <label className="flex items-center justify-between gap-4 text-sm">
            <span>Let the main Secretary and Auditor view this committee's files (read-only)</span>
            <Switch checked={f.oversight} onCheckedChange={(v) => setF({ ...f, oversight: v })} data-testid="committee-oversight-switch" />
          </label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={go} disabled={f.name.trim().length < 2} data-testid="committee-create-submit">Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Committees() {
  const { meta, refresh } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/committees").then((r) => setList(r.data));
  useEffect(() => { if (meta.permissions.manage_users) load(); }, [meta]);
  if (!meta.permissions.manage_users) return <Navigate to="/dashboard" replace />;
  const patch = async (c, body) => { try { await api.patch(`/committees/${c.committee_id}`, body); toast.success("Committee updated"); load(); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <PageHeader eyebrow="Supervision" title="Committee rooms" testid="committees-title">
        <Button onClick={() => setOpen(true)} data-testid="new-committee-button"><Plus className="w-4 h-4 mr-1.5" />New committee</Button>
      </PageHeader>
      <p className="text-sm text-slate-600 mb-5 max-w-2xl">Each committee keeps its own files and runs its own approvals. Committee Admins manage their members. You supervise every room; permanent delete and backups stay with you.</p>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 stagger">
        {list.length === 0 && <div className="text-sm text-slate-500" data-testid="committees-empty">No committees yet. Create the first one.</div>}
        {list.map((c) => (
          <div key={c.committee_id} className={`rounded-lg border bg-white p-5 shadow-sm ${c.active ? "border-slate-200" : "border-dashed border-slate-300 opacity-70"}`} data-testid={`committee-card-${c.committee_id}`}>
            <Link to={`/committees/${c.committee_id}`} className="flex items-center gap-2 font-display font-semibold text-slate-900 hover:underline" data-testid={`committee-open-${c.committee_id}`}><DoorOpen className="w-4 h-4 text-slate-500" />{c.name}</Link>
            {c.description && <p className="text-xs text-slate-500 mt-1">{c.description}</p>}
            <div className="mt-3 text-xs text-slate-600 font-mono">{c.member_count} members · {c.record_count} files</div>
            <div className="mt-4 space-y-2 border-t border-slate-100 pt-3">
              <Label className="flex items-center justify-between text-xs font-normal text-slate-700">Secretary/Auditor oversight
                <Switch checked={!!c.oversight} onCheckedChange={(v) => patch(c, { oversight: v })} data-testid={`committee-oversight-${c.committee_id}`} /></Label>
              <Label className="flex items-center justify-between text-xs font-normal text-slate-700">Room active
                <Switch checked={c.active !== false} onCheckedChange={(v) => patch(c, { active: v })} data-testid={`committee-active-${c.committee_id}`} /></Label>
            </div>
          </div>
        ))}
      </div>
      {open && <CreateDialog onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); refresh(); }} />}
    </div>
  );
}
